import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Plus, 
  Upload, 
  Download, 
  Trash2, 
  Eye,
  Building2
} from 'lucide-react'
import { motion } from 'framer-motion'
import { portfolioTrackerService } from '@/services/portfolioTrackerService'
import { 
  PortfolioTrackerWithSummary,
  AllPortfoliosSummary,
  AllPortfoliosGraphData
} from '@/types/portfolio'
import { formatMoney, formatPercentage } from '@/utils'
import { PageTemplate } from '@/components/templates'
import { Button, Input } from '@/components/atoms'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { CONTAINER_SIZES } from '@/constants'
import { PortfolioChart, PortfolioMetricCard, DonutChart } from './components'
import { MiniAreaChart, MiniBarChart } from '@/components/molecules'


const PortfolioTrackerScreen = () => {
  const navigate = useNavigate()
  const [portfolios, setPortfolios] = useState<PortfolioTrackerWithSummary[]>([])
  const [summary, setSummary] = useState<AllPortfoliosSummary | null>(null)
  const [graphData, setGraphData] = useState<AllPortfoliosGraphData | null>(null)
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [newPortfolioName, setNewPortfolioName] = useState('')
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvPortfolioName, setCsvPortfolioName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [portfoliosData, summaryData, graphData] = await Promise.all([
        portfolioTrackerService.getPortfolios(),
        portfolioTrackerService.getAllPortfoliosSummary(),
        portfolioTrackerService.getAllPortfoliosGraph()
      ])
      
      // Load individual portfolio summaries for card data
      let portfoliosWithSummary: PortfolioTrackerWithSummary[] = portfoliosData.data || []
      
      if (portfoliosData.data && portfoliosData.data.length > 0) {
        const summaryPromises = portfoliosData.data.map(async (portfolio) => {
          try {
            const portfolioSummary = await portfolioTrackerService.getPortfolioSummary(portfolio.portfolio_id)
            return {
              ...portfolio,
              total_invested_amount: portfolioSummary.total_invested_amount,
              total_current_value: portfolioSummary.total_current_value,
              total_gain_loss_usd: portfolioSummary.total_gain_loss_usd,
              total_gain_loss_percentage: portfolioSummary.total_gain_loss_percentage,
              total_assets: portfolioSummary.total_assets
            }
          } catch {
            // If portfolio summary fails, return basic portfolio data
            return {
              ...portfolio,
              total_invested_amount: 0,
              total_current_value: 0,
              total_gain_loss_usd: 0,
              total_gain_loss_percentage: 0,
              total_assets: 0
            }
          }
        })
        
        portfoliosWithSummary = await Promise.all(summaryPromises)
      }
      
      setPortfolios(portfoliosWithSummary)
      setSummary(summaryData)
      setGraphData(graphData)
    } catch (err) {
      setError('Failed to load portfolios')
      console.error('Error loading portfolio data:', err)
      // Set empty defaults to prevent UI breaking
      setPortfolios([])
      setSummary({
        total_invested_amount: 0,
        total_current_value: 0,
        total_gain_loss_usd: 0,
        total_gain_loss_percentage: 0,
        total_assets: 0
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCreatePortfolio = async () => {
    if (!newPortfolioName.trim()) {
      setError('Portfolio name is required')
      return
    }

    try {
      setCreating(true)
      setError(null)
      
      const newPortfolio = await portfolioTrackerService.createPortfolio(newPortfolioName.trim())
      
      // Add new portfolio with summary data (empty portfolio)
      const newPortfolioWithSummary: PortfolioTrackerWithSummary = {
        ...newPortfolio,
        total_invested_amount: 0,
        total_current_value: 0,
        total_gain_loss_usd: 0,
        total_gain_loss_percentage: 0,
        total_assets: 0
      }
      
      setPortfolios(prev => [...(prev || []), newPortfolioWithSummary])
      setNewPortfolioName('')
      setShowCreateModal(false)
      setSuccessMessage('Portfolio created successfully!')
      
      // Reload summary data
      const summaryData = await portfolioTrackerService.getAllPortfoliosSummary()
      setSummary(summaryData)
      
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to create portfolio')
      console.error('Error creating portfolio:', err)
    } finally {
      setCreating(false)
    }
  }

  const handleImportCSV = async () => {
    if (!csvFile || !csvPortfolioName.trim()) {
      setError('Please select a CSV file and enter portfolio name')
      return
    }

    try {
      setImporting(true)
      setError(null)
      
      const result = await portfolioTrackerService.createPortfolioFromCSV(csvPortfolioName.trim(), csvFile)
      
      // Add the new portfolio to the list with summary data
      const newPortfolioWithSummary: PortfolioTrackerWithSummary = {
        portfolio_id: result.portfolio_id,
        name: result.name,
        created_at: result.created_at,
        updated_at: result.updated_at,
        total_invested_amount: 0,
        total_current_value: 0,
        total_gain_loss_usd: 0,
        total_gain_loss_percentage: 0,
        total_assets: result.matched_assets || 0
      }
      
      setPortfolios(prev => [...(prev || []), newPortfolioWithSummary])
      setCsvFile(null)
      setCsvPortfolioName('')
      setShowImportModal(false)
      setSuccessMessage(`Portfolio created with ${result.matched_assets} assets!`)
      
      // Reload summary data
      const summaryData = await portfolioTrackerService.getAllPortfoliosSummary()
      setSummary(summaryData)
      
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err: any) {
      if (portfolioTrackerService.isCSVError(err)) {
        setError(`CSV Error: ${err.error}`)
      } else {
        setError('Failed to import CSV')
      }
      console.error('Error importing CSV:', err)
    } finally {
      setImporting(false)
    }
  }

  const handleDeletePortfolio = async (portfolioId: string, portfolioName: string) => {
    if (!confirm(`Are you sure you want to delete portfolio "${portfolioName}"? This action cannot be undone.`)) {
      return
    }

    try {
      await portfolioTrackerService.deletePortfolio(portfolioId)
      setPortfolios(prev => (prev || []).filter(p => p.portfolio_id !== portfolioId))
      setSuccessMessage('Portfolio deleted successfully!')
      
      // Reload summary data
      const summaryData = await portfolioTrackerService.getAllPortfoliosSummary()
      setSummary(summaryData)
      
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to delete portfolio')
      console.error('Error deleting portfolio:', err)
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.type === 'text/csv') {
      setCsvFile(file)
      setError(null)
    } else {
      setError('Please select a valid CSV file')
      setCsvFile(null)
    }
  }

  const downloadCSVTemplate = () => {
    const template = `Project Name,Date of Investment,Invested Amount,Project Website,Project Valuation,Amount of equity or tokens,Terms
Example Project,2023-01-15,50000,https://example.com,1000000,100000,SAFE agreement`
    
    const blob = new Blob([template], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'portfolio_template.csv'
    a.click()
    window.URL.revokeObjectURL(url)
  }

  if (loading) {
    return <PageTemplate isLoading />
  }

  return (
    <PageTemplate maxWidth={CONTAINER_SIZES.WIDE}>
      <div className="space-y-6">
        {/* Page Header - aligned left as in Figma */}
        <div className="flex flex-col items-start">
          <h1 className="font-['Plus_Jakarta_Sans'] font-semibold leading-[40px] text-[#2d2923] dark:text-[#fdfdfd] text-[30px] tracking-[-0.28px] mb-2">
            Portfolio Tracker
          </h1>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[24px] text-[16px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
            Track and manage your investment portfolios across private markets
          </p>
        </div>
        {/* Success Message */}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-lg"
          >
            {successMessage}
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg"
          >
            {error}
          </motion.div>
        )}

        {/* Summary Statistics */}
        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-2">
            <PortfolioMetricCard
              icon="invested"
              label="Total Invested"
              value={formatMoney(summary.total_invested_amount)}
              chart={
                <DonutChart
                  size={80}
                  strokeWidth={10}
                  segments={[
                    { value: 109, color: '#c8ed03' },
                    { value: 320, color: 'rgba(45,41,35,0.48)' },
                    { value: 45, color: 'rgba(45,41,35,0.8)' },
                  ]}
                  centerContent={
                    <div className="flex flex-col items-center justify-center rotate-[90deg]">
                      <span className="text-[10px] font-bold text-[#c8ed03]">109</span>
                      <span className="text-[12px] font-bold text-[#2d2923] dark:text-[#fdfdfd]">320</span>
                      <span className="text-[10px] font-bold text-[rgba(45,41,35,0.48)]">45</span>
                    </div>
                  }
                />
              }
            />
            <PortfolioMetricCard
              icon="current"
              label="Current Value"
              value={formatMoney(summary.total_current_value)}
              chart={<MiniAreaChart className="!static !w-[100px] !h-[60px]" />}
            />
            <PortfolioMetricCard
              icon="gain"
              label="Total Gain/Loss"
              value={formatMoney(summary.total_gain_loss_usd)}
              subValue={formatPercentage(summary.total_gain_loss_percentage)}
              trend={summary.total_gain_loss_usd >= 0 ? 'up' : 'down'}
              chart={
                <MiniBarChart 
                  className="!static !w-[100px] !h-[60px]" 
                  showAverage={false}
                  data={[40, 60, 30, 80, 20, 90, 40, 60, 30, 80]}
                />
              }
            />
            <PortfolioMetricCard
              icon="assets"
              label="Total Assets"
              value={summary.total_assets.toString()}
              chart={
                <DonutChart
                  size={80}
                  strokeWidth={10}
                  segments={[
                    { value: 4, color: '#c8ed03' },
                    { value: 1, color: 'rgba(45,41,35,0.12)' },
                  ]}
                  centerContent={
                    <span className="text-[18px] font-bold text-[#2d2923] dark:text-[#fdfdfd] rotate-[90deg]">
                      {summary.total_assets}
                    </span>
                  }
                />
              }
            />
          </div>
        )}

        {/* Portfolio Performance Chart */}
        <PortfolioChart
          graphData={graphData}
          loading={loading}
          title="All Portfolios Performance"
        />

        {/* Portfolio List */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <h2 className="font-['Plus_Jakarta_Sans'] font-semibold leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] text-[24px] tracking-[-0.2px]">
              Your Portfolios
            </h2>
            
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-[36px] px-4 text-[13px] font-medium border-[rgba(45,41,35,0.12)]"
                leftIcon={<Download size={14} />}
                onClick={downloadCSVTemplate}
              >
                Download Template
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                className="h-[36px] px-4 text-[13px] font-medium border-[rgba(45,41,35,0.12)] bg-[rgba(45,41,35,0.04)]"
                leftIcon={<Upload size={14} />}
                onClick={() => setShowImportModal(true)}
              >
                Import CSV
              </Button>

              <Button
                variant="primary"
                size="sm"
                className="h-[36px] px-4 text-[13px] font-medium bg-[#121212] hover:bg-[#222222] text-white"
                leftIcon={<Plus size={14} />}
                onClick={() => navigate('/portfolio/create')}
              >
                Create Portfolio
              </Button>
            </div>
          </div>

          {!portfolios || portfolios.length === 0 ? (
            <Card variant="elevated">
              <CardContent className="p-12 text-center">
                <Building2 className="mx-auto text-muted-foreground mb-4" size={48} />
                <h3 className="text-lg font-semibold mb-2">No portfolios yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first portfolio to start tracking your investments
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="primary"
                    leftIcon={<Plus size={16} />}
                    onClick={() => navigate('/portfolio/create')}
                  >
                    Create Portfolio
                  </Button>
                  <Button
                    variant="secondary"
                    leftIcon={<Upload size={16} />}
                    onClick={() => setShowImportModal(true)}
                  >
                    Import CSV
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {portfolios?.map((portfolio) => (
                <Card key={portfolio.portfolio_id} variant="elevated" className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{portfolio.name}</CardTitle>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/portfolio/${portfolio.portfolio_id}`)}
                        >
                          <Eye size={16} />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePortfolio(portfolio.portfolio_id, portfolio.name)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      <div className="flex flex-col">
                        <span className="text-[12px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">Total Invested:</span>
                        <span className="text-[14px] font-medium text-[#2d2923] dark:text-[#fdfdfd]">{formatMoney(portfolio.total_invested_amount)}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[12px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">Profit/Loss:</span>
                        <span className={`text-[14px] font-medium ${
                          (portfolio.total_gain_loss_usd || 0) >= 0 ? 'text-[#2ea66b]' : 'text-[#ff540c]'
                        }`}>
                          {formatMoney(portfolio.total_gain_loss_usd)} <span className="text-[12px] ml-1">{formatPercentage(portfolio.total_gain_loss_percentage)}</span>
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[12px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">Assets:</span>
                        <span className="text-[14px] font-medium text-[#2d2923] dark:text-[#fdfdfd]">{portfolio.total_assets || 0}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[12px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">Updated:</span>
                        <span className="text-[14px] font-medium text-[#2d2923] dark:text-[#fdfdfd]">{new Date(portfolio.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-[rgba(45,41,35,0.08)]">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full h-[36px] text-[13px] font-medium border-[rgba(45,41,35,0.12)]"
                        onClick={() => navigate(`/portfolio/${portfolio.portfolio_id}`)}
                      >
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

        {/* Create Portfolio Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle>Create New Portfolio</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Portfolio Name</label>
                  <Input
                    value={newPortfolioName}
                    onChange={(e) => setNewPortfolioName(e.target.value)}
                    placeholder="Enter portfolio name"
                    onKeyPress={(e) => e.key === 'Enter' && handleCreatePortfolio()}
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleCreatePortfolio}
                    disabled={creating || !newPortfolioName.trim()}
                    className="flex-1"
                  >
                    {creating ? 'Creating...' : 'Create'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Import CSV Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle>Import Portfolio from CSV</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Portfolio Name</label>
                  <Input
                    value={csvPortfolioName}
                    onChange={(e) => setCsvPortfolioName(e.target.value)}
                    placeholder="Enter portfolio name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">CSV File</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="w-full p-2 border border-input rounded-md"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Required columns: Project Name, Date of Investment, Invested Amount
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowImportModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleImportCSV}
                    disabled={importing || !csvFile || !csvPortfolioName.trim()}
                    className="flex-1"
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
    </PageTemplate>
  )
}

export default PortfolioTrackerScreen
